import React, { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { randomInt } from 'client/utils/seed-randomizers';

import { scroller } from 'react-scroll';
import { SCROLL_DURATION } from 'site-modules/shared/constants/sub-navigation';
import { scrollSpy } from 'site-modules/shared/components/scroll-link/scroll-spy';

import Label from 'reactstrap/lib/Label';
import { StyledSelect } from 'site-modules/shared/components/styled-select/styled-select';

/*
 Checks for isMobile to get scroll offset
 */
const SCROLL_OFFSET = {
  true: -62, // mobile
  false: -95, // desktop
};

export const hashSpy = {
  current: null,
  previous: null,
  isUpdated: false,
  isSelectChanged: false,
  numberSpied: 0,
};

export function onSpyInit({ targetElement, hash, offset, handleSetActive }) {
  if (!targetElement) {
    return;
  }

  const { top, bottom } = targetElement.getBoundingClientRect();
  const isActiveNow = top <= -offset && bottom >= -offset;

  if (isActiveNow) {
    hashSpy.numberSpied += 1;
    handleSetActive(hash);
  }
}

export function AnchorNavContentDropdown({
  navList,
  linkTrackingId,
  className,
  listClassName,
  optionHash,
  setOptionHash,
  labelTitle,
  labelTitleClassName,
  isMobile,
}) {
  const scrollToHash = useCallback(
    /*
      Gets called when select is changed.
    */
    value => {
      const { hash } = value;
      const targetElement = document.getElementById(hash);
      if (!targetElement) {
        return;
      }
      hashSpy.isSelectChanged = true;
      scroller.scrollTo(hash, {
        duration: SCROLL_DURATION,
        offset: SCROLL_OFFSET[isMobile],
        smooth: true,
      });
      setOptionHash(hash);
      if (!hashSpy.isUpdated) {
        history.pushState({}, window.document.title, `#${hash}`);
      } else {
        hashSpy.isUpdated = false;
      }
    },
    [isMobile, setOptionHash]
  );

  const selectId = useRef(`jump-sel-${randomInt()}`);

  const handleSetActive = hash => {
    setOptionHash(hash);
    const isScrollOnly = !hashSpy.isUpdated && !hashSpy.isSelectChanged;
    if (isScrollOnly) {
      hashSpy.current = hash;
      if (hashSpy.current !== hashSpy.previous) {
        if (hashSpy.previous) {
          history.pushState({}, window.document.title, `#${hash}`);
        }
        hashSpy.previous = hash;
      }
    }

    if (hashSpy.numberSpied === 2) {
      /*
       reset
       */
      hashSpy.numberSpied = 0;
      if (hashSpy.isUpdated) {
        hashSpy.isUpdated = false;
      }
      if (hashSpy.isSelectChanged) {
        hashSpy.isSelectChanged = false;
      }
    }
  };

  useEffect(() => {
    const scrollSpies = [];
    const offset = SCROLL_OFFSET[isMobile] - 6;
    if (hashSpy.numberSpied > 1) {
      hashSpy.isUpdated = true;
    }
    navList.forEach(({ hash }) => {
      const handleSpy = debounce(() => {
        const targetElement = document.getElementById(hash);
        onSpyInit({ targetElement, hash, offset, handleSetActive });
      }, 200);

      scrollSpy.addSpy(handleSpy);
      scrollSpies.push(handleSpy);
    });

    return () => {
      scrollSpies.forEach(handleSpy => {
        scrollSpy.removeSpy(handleSpy);
      });
    };
  }, [handleSetActive, isMobile, navList, setOptionHash]);

  return (
    <div className={classnames('pos-r d-flex align-items-center', className)}>
      {labelTitle && (
        <Label for={selectId.current} className={labelTitleClassName}>
          {labelTitle}
        </Label>
      )}
      <StyledSelect
        className={classnames('rounded size-16 ps-0_75 pe-2 text-start mb-0 text-capitalize', listClassName)}
        id={selectId.current}
        data-tracking-id={linkTrackingId}
        onChange={scrollToHash}
        value={optionHash}
        name={selectId.current}
        valueKey="hash"
        labelKey="name"
        options={navList}
      />
    </div>
  );
}

AnchorNavContentDropdown.propTypes = {
  navList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      hash: PropTypes.string,
    })
  ).isRequired,
  linkTrackingId: PropTypes.string,
  className: PropTypes.string,
  listClassName: PropTypes.string,
  optionHash: PropTypes.string,
  setOptionHash: PropTypes.func.isRequired,
  labelTitle: PropTypes.string,
  labelTitleClassName: PropTypes.string,
  isMobile: PropTypes.bool,
};

AnchorNavContentDropdown.defaultProps = {
  linkTrackingId: 'view_content',
  className: null,
  listClassName: null,
  optionHash: '',
  labelTitle: null,
  labelTitleClassName: null,
  isMobile: false,
};
